import { useEffect, useMemo, useState } from 'react';
import { TableSkeleton } from '../../components/enterprise';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AllInboxOutlined as InboxIcon,
  RefreshOutlined as RefreshIcon,
  DeleteOutlineOutlined as DeleteIcon,
  SendOutlined as SendIcon,
  ChevronLeftOutlined as BackIcon,
  CheckCircleOutlineOutlined as ResolveIcon,
  PauseCircleOutlineOutlined as WaitIcon,
  PlayCircleOutlineOutlined as InProgressIcon,
  BugReportOutlined as BugIcon,
  LightbulbOutlined as FeatureIcon,
  HelpOutlineOutlined as GeneralIcon,
  SupportAgentOutlined as OperatorIcon,
  PersonOutlined as UserIcon,
} from '@mui/icons-material';
import {
  fetchOperatorInbox,
  fetchOperatorTicket,
  replyAsOperator,
  changeOperatorStatus,
  deleteOperatorTicket,
  STATUS_COLORS,
  STATUS_LABELS,
  TYPE_LABELS,
} from '../../services/contactApi';
import { toServerDate } from '../../timezone';

/**
 * Operator-side ticket inbox.
 *
 * <p>Two-pane: counts strip + filter tabs + sortable table on the left,
 * thread + reply composer on the right when a row is selected.
 */
const TYPE_ICON = {
  BUG: BugIcon,
  FEATURE: FeatureIcon,
  GENERAL: GeneralIcon,
};

const TYPE_COLOR = {
  BUG: 'error',
  FEATURE: 'secondary',
  GENERAL: 'info',
};

const FILTER_TABS = [
  { id: 'AWAITING', label: 'Awaiting operator' },
  { id: 'OPEN', label: 'Open' },
  { id: 'IN_PROGRESS', label: 'In progress' },
  { id: 'WAITING_USER', label: 'Waiting on user' },
  { id: 'RESOLVED', label: 'Resolved' },
  { id: 'ALL', label: 'All' },
];

const formatTimestamp = (ts) => {
  if (!ts) return '—';
  try {
    return toServerDate(ts)?.toLocaleString() ?? ts;
  } catch {
    return ts;
  }
};

const StatCard = ({ label, value, accent }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.5,
      borderRadius: 1.5,
      flex: 1,
      minWidth: 110,
      borderLeft: '3px solid',
      borderLeftColor: accent || 'primary.main',
    }}
  >
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h5" fontWeight={700}>
      {value ?? 0}
    </Typography>
  </Paper>
);

const Bubble = ({ author, byOperator, body, createdAt }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
      <Avatar
        sx={{
          width: 32,
          height: 32,
          bgcolor: byOperator ? 'primary.main' : 'grey.400',
        }}
      >
        {byOperator ? <OperatorIcon sx={{ fontSize: 16 }} /> : <UserIcon sx={{ fontSize: 16 }} />}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.5 }}>
          <Typography variant="body2" fontWeight={600}>
            {author || (byOperator ? 'Operator' : 'User')}
          </Typography>
          {byOperator && (
            <Chip label="Operator" size="small" color="primary" sx={{ height: 16, fontSize: '0.6rem' }} />
          )}
          <Typography variant="caption" color="text.secondary">
            {formatTimestamp(createdAt)}
          </Typography>
        </Stack>
        <Paper
          variant="outlined"
          sx={{
            p: 1.25,
            borderRadius: 1.5,
            backgroundColor: byOperator
              ? (isDark ? 'rgba(110,168,254,0.06)' : 'rgba(9,105,218,0.04)')
              : 'background.default',
            borderColor: byOperator ? 'primary.light' : 'divider',
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {body}
          </Typography>
        </Paper>
      </Box>
    </Stack>
  );
};

const OperatorContactInboxPage = () => {
  const theme = useTheme();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('AWAITING');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [reply, setReply] = useState('');
  const [replyStatus, setReplyStatus] = useState('IN_PROGRESS');
  const [submitting, setSubmitting] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await fetchOperatorInbox();
      setSummary(data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load inbox');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    fetchOperatorTicket(selectedId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.message || e.message || 'Failed to load ticket');
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const tickets = summary?.tickets ?? [];

  const filtered = useMemo(() => {
    let out = tickets;
    if (filter === 'AWAITING') {
      out = out.filter((t) => !t.lastReplyByOperator && t.status !== 'RESOLVED');
    } else if (filter !== 'ALL') {
      out = out.filter((t) => t.status === filter);
    }
    if (typeFilter !== 'ALL') {
      out = out.filter((t) => t.type === typeFilter);
    }
    return out;
  }, [tickets, filter, typeFilter]);

  const filterCount = (id) => {
    if (id === 'ALL') return tickets.length;
    if (id === 'AWAITING') return summary?.awaitingOperator ?? 0;
    return tickets.filter((t) => t.status === id).length;
  };

  const handleReply = async () => {
    if (!reply.trim() || !selectedId || submitting) return;
    setSubmitting(true);
    try {
      const updated = await replyAsOperator(selectedId, reply.trim(), replyStatus);
      setDetail(updated);
      setReply('');
      // Refresh inbox so counts and "needs attention" sort update.
      reload();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Reply failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatus = async (status) => {
    if (!selectedId) return;
    try {
      const updated = await changeOperatorStatus(selectedId, status);
      setDetail(updated);
      reload();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Could not change status');
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    if (!window.confirm('Delete this ticket and all its replies? This cannot be undone.')) return;
    try {
      await deleteOperatorTicket(selectedId);
      setSelectedId(null);
      setDetail(null);
      reload();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Could not delete ticket');
    }
  };

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <InboxIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h4" fontWeight={700}>
              Contact inbox
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            User-submitted bug reports, feature requests, and general contact.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={reload} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Stats strip */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <StatCard label="Awaiting you" value={summary?.awaitingOperator} accent="warning.main" />
        <StatCard label="Open" value={summary?.open} accent="info.main" />
        <StatCard label="In progress" value={summary?.inProgress} accent="primary.main" />
        <StatCard label="Waiting on user" value={summary?.waitingUser} accent="secondary.main" />
        <StatCard label="Resolved" value={summary?.resolved} accent="success.main" />
        <StatCard label="Bug reports" value={summary?.bug} accent="error.main" />
        <StatCard label="Feature requests" value={summary?.feature} accent="secondary.main" />
        <StatCard label="General" value={summary?.general} accent="grey.500" />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
        {/* Left pane — list */}
        <Paper variant="outlined" sx={{ flex: selectedId ? 0.55 : 1, display: 'flex', flexDirection: 'column', borderRadius: 1.5, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
            <Tabs
              value={filter}
              onChange={(_, v) => setFilter(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ minHeight: 40, flex: 1, '& .MuiTab-root': { minHeight: 40, fontSize: '0.78rem', textTransform: 'none' } }}
            >
              {FILTER_TABS.map((t) => (
                <Tab key={t.id} value={t.id} label={`${t.label} (${filterCount(t.id)})`} />
              ))}
            </Tabs>
            <FormControl size="small" sx={{ minWidth: 130, mr: 1 }}>
              <InputLabel>Type</InputLabel>
              <Select label="Type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <MenuItem value="ALL">All types</MenuItem>
                <MenuItem value="BUG">Bug reports</MenuItem>
                <MenuItem value="FEATURE">Feature requests</MenuItem>
                <MenuItem value="GENERAL">General</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <TableSkeleton rows={5} columns={4} />
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Nothing here. Quiet day.
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: 110 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Org / User</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 130 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 170 }}>Last activity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((t) => {
                    const Icon = TYPE_ICON[t.type] || GeneralIcon;
                    const awaitingOp = !t.lastReplyByOperator && t.status !== 'RESOLVED';
                    return (
                      <TableRow
                        key={t.id}
                        hover
                        selected={t.id === selectedId}
                        onClick={() => setSelectedId(t.id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Chip
                            icon={<Icon sx={{ fontSize: 14 }} />}
                            label={TYPE_LABELS[t.type] || t.type}
                            size="small"
                            color={TYPE_COLOR[t.type] || 'default'}
                            variant="outlined"
                            sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="body2" sx={{ fontWeight: awaitingOp ? 700 : 500 }}>
                              {t.subject}
                            </Typography>
                            {awaitingOp && (
                              <Chip
                                label="Needs reply"
                                size="small"
                                color="warning"
                                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
                              />
                            )}
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {t.replyCount > 0 ? `${t.replyCount} repl${t.replyCount === 1 ? 'y' : 'ies'}` : 'No replies yet'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                            {t.organizationName || `Org #${t.organizationId}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t.userName} ({t.userEmail})
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={STATUS_LABELS[t.status] || t.status}
                            color={STATUS_COLORS[t.status] || 'default'}
                            size="small"
                            sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {formatTimestamp(t.lastReplyAt || t.updatedAt)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Right pane — thread + reply */}
        {selectedId && (
          <Paper variant="outlined" sx={{ flex: 0.45, display: 'flex', flexDirection: 'column', borderRadius: 1.5, minWidth: 0 }}>
            {detailLoading || !detail ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <TableSkeleton rows={4} columns={3} />
              </Box>
            ) : (
              <>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <IconButton size="small" onClick={() => setSelectedId(null)}>
                    <BackIcon />
                  </IconButton>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={700} noWrap>
                      {detail.subject}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {detail.organizationName || `Org #${detail.organizationId}`} ·{' '}
                      {detail.userName} ({detail.userEmail})
                    </Typography>
                  </Box>
                  <Chip
                    label={STATUS_LABELS[detail.status] || detail.status}
                    color={STATUS_COLORS[detail.status] || 'default'}
                    size="small"
                  />
                </Stack>

                {/* Meta */}
                <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', backgroundColor: 'background.default' }}>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                    <Chip label={TYPE_LABELS[detail.type] || detail.type} size="small" variant="outlined" />
                    {detail.severity && <Chip label={`Severity: ${detail.severity}`} size="small" variant="outlined" />}
                    {detail.pageUrl && (
                      <Tooltip title={detail.pageUrl}>
                        <Chip label="Has page URL" size="small" variant="outlined" component="a" href={detail.pageUrl} target="_blank" clickable />
                      </Tooltip>
                    )}
                  </Stack>
                </Box>

                {/* Thread */}
                <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                  <Bubble
                    author={detail.userName}
                    byOperator={false}
                    body={detail.body}
                    createdAt={detail.createdAt}
                  />
                  {detail.replies?.map((r) => (
                    <Bubble
                      key={r.id}
                      author={r.authorName}
                      byOperator={r.byOperator}
                      body={r.body}
                      createdAt={r.createdAt}
                    />
                  ))}
                </Box>

                <Divider />

                {/* Composer */}
                <Box sx={{ p: 2 }}>
                  <TextField
                    placeholder="Reply to the user…"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    multiline
                    minRows={4}
                    fullWidth
                    inputProps={{ maxLength: 20000 }}
                  />
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5, flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <InputLabel>Set status</InputLabel>
                      <Select label="Set status" value={replyStatus} onChange={(e) => setReplyStatus(e.target.value)}>
                        <MenuItem value="IN_PROGRESS">In progress</MenuItem>
                        <MenuItem value="WAITING_USER">Waiting on user</MenuItem>
                        <MenuItem value="RESOLVED">Resolved</MenuItem>
                      </Select>
                    </FormControl>
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title="Mark as in progress (no reply)">
                      <IconButton onClick={() => handleStatus('IN_PROGRESS')} size="small">
                        <InProgressIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Waiting on user (no reply)">
                      <IconButton onClick={() => handleStatus('WAITING_USER')} size="small">
                        <WaitIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Mark resolved (no reply)">
                      <IconButton onClick={() => handleStatus('RESOLVED')} size="small" color="success">
                        <ResolveIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete ticket">
                      <IconButton onClick={handleDelete} size="small" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                    <Button
                      variant="contained"
                      startIcon={<SendIcon />}
                      onClick={handleReply}
                      disabled={!reply.trim() || submitting}
                    >
                      {submitting ? 'Sending…' : 'Send reply'}
                    </Button>
                  </Stack>
                </Box>
              </>
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default OperatorContactInboxPage;
